import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { FiHelpCircle, FiUser } from "react-icons/fi";

const ALL_ORGANIZATIONS = "all";

import type { IdentityUser } from "../auth";
import { shellBootstrapKey, shellBootstrapQuery } from "./bootstrap";
import type { ShellServices } from "./services";
import { ErpLogo } from "./BrandLogo";
import { NotificationCenter } from "./NotificationCenter";
import { CloseButton } from "./CloseButton";
import { LanguageSelect, TimezoneSelect } from "./ProfileFields";
import { formatPhone } from "./formatting";
import { workspaceNavigation } from "./workspaceNavigation";
import { Select } from "./Select";
import { Notice } from "./Notice";
import { authRateLimitMessage, useAuthRateLimit } from "./useAuthRateLimit";

function moduleInitial(label: string): string {
  return label.trim().charAt(0).toLocaleUpperCase("ru") || "•";
}

const AVATAR_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];
// Совпадает с AVATAR_MAX_SIZE_BYTES в ядре; сервер всё равно проверит сам,
// здесь лишь не даём отправлять заведомо большой файл.
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

function HeaderAvatar({
  user,
  services,
  large = false,
}: {
  user: IdentityUser;
  services: ShellServices;
  large?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const fileId = user.avatar?.file_id;
  const download = useQuery({
    queryKey: ["files", fileId, "download"],
    queryFn: () => services.files.download(fileId ?? ""),
    enabled: fileId !== undefined,
    staleTime: 60_000,
  });
  const source = download.data?.data.url;

  return (
    <span
      className={`user-avatar${large ? " user-avatar--large" : ""}`}
      aria-hidden="true"
    >
      {source !== undefined && !failed ? (
        <img src={source} alt="" onError={() => setFailed(true)} />
      ) : (
        moduleInitial(user.display_name)
      )}
    </span>
  );
}

function ProfileCard({
  user,
  services,
  onClose,
  onLogout,
  loggingOut,
}: {
  user: IdentityUser;
  services: ShellServices;
  onClose: () => void;
  onLogout: () => void;
  loggingOut: boolean;
}) {
  const queryClient = useQueryClient();
  const [avatarError, setAvatarError] = useState<string | null>(null);
  // Фото меняется сразу, без кнопки «Сохранить»: это отдельная операция API,
  // и после неё карточка остаётся открытой.
  const refreshProfile = () =>
    queryClient.invalidateQueries({ queryKey: shellBootstrapKey });
  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      const upload = await services.files.initiate({
        purpose: "avatar",
        filename: file.name,
        mime_type: file.type,
        size: file.size,
      });
      await services.files.uploadObject(upload.upload, file);
      await services.files.confirm(upload.data.id);
      return services.identity.assignAvatar(upload.data.id);
    },
    onSuccess: refreshProfile,
    onError: () =>
      setAvatarError(
        "Не удалось загрузить фото. Проверьте файл и повторите попытку.",
      ),
  });
  const chooseAvatar = (file: File) => {
    setAvatarError(null);
    if (!AVATAR_MIME_TYPES.includes(file.type)) {
      setAvatarError("Поддерживаются JPEG, PNG и WebP.");
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError("Файл больше 5 МБ. Выберите фото поменьше.");
      return;
    }
    uploadAvatar.mutate(file);
  };
  const removeAvatar = useMutation({
    mutationFn: () => services.identity.removeAvatar(),
    onSuccess: refreshProfile,
    onError: () =>
      setAvatarError("Не удалось удалить фото. Повторите попытку."),
  });
  const avatarPending = uploadAvatar.isPending || removeAvatar.isPending;
  const canUploadAvatar = user.permissions.includes("files.upload");
  const [values, setValues] = useState({
    first_name: user.first_name ?? "",
    last_name: user.last_name ?? "",
    display_name: user.display_name,
    phone: user.phone ?? "",
    language: user.language,
    timezone: user.timezone,
  });
  const [error, setError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState(user.email);
  const [emailNotice, setEmailNotice] = useState<string | null>(null);
  const emailRateLimit = useAuthRateLimit();
  const save = useMutation({
    mutationFn: () =>
      services.identity.updateProfile({
        ...values,
        phone: values.phone || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: shellBootstrapKey });
      onClose();
    },
    onError: () =>
      setError(
        "Не удалось сохранить профиль. Проверьте данные и повторите попытку.",
      ),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    save.mutate();
  };
  const emailChange = useMutation({
    mutationFn: () => services.identity.requestEmailChange(newEmail),
    onSuccess: (result) =>
      setEmailNotice(
        `Письмо с подтверждением отправлено на ${result.pending_email}. Email изменится только после перехода по ссылке из письма.`,
      ),
    onError: (error) =>
      setEmailNotice(
        emailRateLimit.handleError(error)
          ? authRateLimitMessage
          : "Не удалось отправить письмо. Проверьте адрес и повторите попытку.",
      ),
  });
  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-card-title"
    >
      <button
        className="modal-backdrop"
        type="button"
        aria-label="Закрыть карточку"
        onClick={onClose}
      />
      <form className="modal-card profile-card" onSubmit={submit}>
        <div className="modal-title">
          <div>
            <p className="eyebrow">Мой профиль</p>
            <h2 id="profile-card-title">Личная информация</h2>
          </div>
          <CloseButton onClick={onClose} />
        </div>
        <section
          className="profile-avatar"
          aria-labelledby="profile-avatar-title"
        >
          <HeaderAvatar
            key={user.avatar?.file_id ?? "none"}
            user={user}
            services={services}
            large
          />
          <div className="profile-avatar__body">
            <h3 id="profile-avatar-title">Фото</h3>
            {canUploadAvatar ? (
              <div className="profile-avatar__actions">
                <label className="button button--secondary button--compact">
                  <span>
                    {uploadAvatar.isPending
                      ? "Загружаем…"
                      : user.avatar
                        ? "Изменить фото"
                        : "Загрузить фото"}
                  </span>
                  <input
                    className="visually-hidden"
                    type="file"
                    aria-label="Файл фото профиля"
                    accept={AVATAR_MIME_TYPES.join(",")}
                    disabled={avatarPending}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        chooseAvatar(file);
                      }
                      event.currentTarget.value = "";
                    }}
                  />
                </label>
                {user.avatar ? (
                  <button
                    className="button button--text button--compact"
                    type="button"
                    disabled={avatarPending}
                    onClick={() => {
                      setAvatarError(null);
                      removeAvatar.mutate();
                    }}
                  >
                    {removeAvatar.isPending ? "Удаляем…" : "Удалить фото"}
                  </button>
                ) : null}
              </div>
            ) : (
              <p className="profile-avatar__hint">
                Чтобы загрузить фото, нужно право на загрузку файлов. Обратитесь
                к администратору.
              </p>
            )}
            {canUploadAvatar ? (
              <p className="profile-avatar__hint">
                JPEG, PNG или WebP, до 5 МБ.
              </p>
            ) : null}
            {avatarError ? <Notice>{avatarError}</Notice> : null}
          </div>
        </section>
        <section
          className="profile-email-change"
          aria-labelledby="profile-email-title"
        >
          <h3 id="profile-email-title">Email</h3>
          <p className="profile-email">Текущий: {user.email}</p>
          <label>
            <span>Новый email</span>
            <input
              aria-label="Новый email"
              type="email"
              required
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
            />
          </label>
          <button
            className="button button--secondary button--compact"
            type="button"
            disabled={
              emailChange.isPending ||
              emailRateLimit.blocked ||
              newEmail === user.email
            }
            onClick={() => {
              setEmailNotice(null);
              emailChange.mutate();
            }}
          >
            {emailChange.isPending
              ? "Отправляем…"
              : "Отправить письмо для подтверждения"}
          </button>
          {emailNotice ? (
            emailNotice.startsWith("Не удалось") ? (
              <Notice>{emailNotice}</Notice>
            ) : (
              <p className="profile-email-notice" role="status">
                {emailNotice}
              </p>
            )
          ) : null}
        </section>
        <div className="user-form">
          <label>
            <span>Имя</span>
            <input
              required
              value={values.first_name}
              onChange={(event) =>
                setValues({ ...values, first_name: event.target.value })
              }
            />
          </label>
          <label>
            <span>Фамилия</span>
            <input
              required
              value={values.last_name}
              onChange={(event) =>
                setValues({ ...values, last_name: event.target.value })
              }
            />
          </label>
          <label>
            <span>Отображаемое имя</span>
            <input
              required
              value={values.display_name}
              onChange={(event) =>
                setValues({ ...values, display_name: event.target.value })
              }
            />
          </label>
          <label>
            <span>Телефон</span>
            <input
              type="tel"
              inputMode="tel"
              placeholder="+7 (999) 123-45-67"
              value={values.phone}
              onChange={(event) =>
                setValues({ ...values, phone: formatPhone(event.target.value) })
              }
            />
          </label>
          <label>
            <span>Язык</span>
            <LanguageSelect
              value={values.language}
              onChange={(value) => setValues({ ...values, language: value })}
            />
          </label>
          <label>
            <span>Часовой пояс</span>
            <TimezoneSelect
              value={values.timezone}
              onChange={(value) => setValues({ ...values, timezone: value })}
            />
          </label>
        </div>
        {error ? <Notice>{error}</Notice> : null}
        <div className="modal-actions profile-actions">
          <button
            className="button button--secondary"
            type="button"
            disabled={loggingOut}
            onClick={onLogout}
          >
            Выйти
          </button>
          <button
            className="button button--primary"
            type="submit"
            disabled={save.isPending}
          >
            {save.isPending ? "Сохраняем…" : "Сохранить"}
          </button>
        </div>
      </form>
    </div>
  );
}

export function ErpShell({ services }: { services: ShellServices }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const mobileMenuPanel = useRef<HTMLElement>(null);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }
    // Фокус уходит на саму панель, а не на первую ссылку: программный фокус на
    // ссылке в Safari на iOS рисовал вокруг «Документооборота» рамку.
    mobileMenuPanel.current?.focus({ preventScroll: true });
    // Пока меню открыто, страница под ним не прокручивается: иначе iOS отдаёт
    // первое касание странице, и список в меню начинает листаться не сразу.
    document.body.classList.add("mobile-menu-open");
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("mobile-menu-open");
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileMenuOpen]);

  const scopeMutation = useMutation({
    mutationFn: async (scopeId: string | null) => {
      if (scopeId === null) {
        await services.administration.selectAllScopes();
        return;
      }
      await services.administration.selectScope(scopeId);
    },
    onSuccess: async () => {
      // Организация хранится в сессии на сервере, и от неё зависит почти всё,
      // что показано на экране: люди, файлы, документы, модули. Раньше
      // сбрасывалась только часть, и после переключения приходилось обновлять
      // страницу руками. Теперь убираем все загруженные данные, кроме самой
      // оболочки, — она перечитается следующей строкой.
      queryClient.removeQueries({
        predicate: ({ queryKey }) => queryKey[0] !== "shell",
      });
      await queryClient.invalidateQueries({ queryKey: shellBootstrapKey });
    },
  });
  const logoutMutation = useMutation({
    mutationFn: () => services.identity.logout(),
    onSettled: async () => {
      queryClient.removeQueries({ queryKey: ["shell"] });
      await navigate({ to: "/login" });
    },
  });

  if (bootstrap.data === undefined) {
    return null;
  }

  const { user, scopes, currentScope, navigation, allOrganizations } =
    bootstrap.data;
  const routeScopeId = pathname.match(
    /^\/administration\/scopes\/([^/]+)/,
  )?.[1];
  const isOrganizationManagementRoute = pathname.startsWith(
    "/administration/scopes",
  );
  const routeScope = scopes.find((scope) => scope.id === routeScopeId);
  const workspaceName =
    routeScope?.name ?? currentScope?.name ?? "Алтай Эко Парк";
  // Порядок задаёт бэкенд: головная организация первой, остальные по названию.
  const availableScopes = scopes;
  const navigationItems = workspaceNavigation({
    permissions: user.permissions,
    modules: navigation,
  });
  const canViewNotifications = navigationItems.some(
    (item) => item.key === "notifications",
  );
  const canViewDocumentWorkflowGuide =
    user.permissions.includes("document-workflow.view") &&
    navigationItems.some((item) =>
      item.key.startsWith("module:document-workflow:"),
    );
  const nav = (
    <nav className="sidebar-nav" aria-label="Разделы ERP">
      <ul>
        {navigationItems.map((item) => (
          <li key={item.key}>
            <SidebarNavigationLink item={item} />
          </li>
        ))}
      </ul>
    </nav>
  );
  // Общий для десктопного сайдбара и мобильного меню. Безопасность, сессии и
  // сведения о приложении собраны на одной странице: в меню под них не хватало
  // места, и оно начинало прокручиваться.
  const accountSectionActive =
    pathname === "/about" ||
    pathname === "/account" ||
    pathname.startsWith("/account/");
  const accountNav = (
    <nav className="sidebar-account-nav" aria-label="Настройки аккаунта">
      <ul>
        <li>
          <Link
            to="/account"
            className={`sidebar-link${accountSectionActive ? " sidebar-link--active" : ""}`}
          >
            <span className="nav-icon" aria-hidden="true">
              <FiUser />
            </span>
            <span>Аккаунт</span>
          </Link>
        </li>
        {canViewDocumentWorkflowGuide ? (
          <li>
            <Link
              to="/guide"
              className={`sidebar-link${pathname === "/guide" ? " sidebar-link--active" : ""}`}
            >
              <span className="nav-icon" aria-hidden="true">
                <FiHelpCircle />
              </span>
              <span>Инструкция</span>
            </Link>
          </li>
        ) : null}
      </ul>
    </nav>
  );

  return (
    <div className="erp-layout">
      <aside className="sidebar">
        <ErpLogo label="Алтайэкопарк ERP — на главную" />
        {nav}
        {accountNav}
        <p className="sidebar-caption">Внутренняя система</p>
      </aside>

      <header className="erp-header">
        <button
          className="menu-button"
          type="button"
          aria-label="Открыть меню"
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-navigation"
          onClick={() => setMobileMenuOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className="header-context">
          <span className="header-context__label">Рабочее пространство</span>
          <strong>{workspaceName}</strong>
        </div>
        <div className="header-actions">
          {scopes.length > 0 ? (
            <label className="scope-select">
              <span>Рабочая организация</span>
              <Select
                aria-label="Рабочая организация"
                value={
                  allOrganizations
                    ? ALL_ORGANIZATIONS
                    : currentScope
                      ? `scope:${currentScope.id}`
                      : ""
                }
                disabled={scopeMutation.isPending}
                onChange={(event) => {
                  void (async () => {
                    const value = event.target.value;
                    if (value === ALL_ORGANIZATIONS) {
                      await scopeMutation.mutateAsync(null);
                      if (isOrganizationManagementRoute) {
                        await navigate({ to: "/administration/scopes" });
                      }
                      return;
                    }
                    if (value.startsWith("scope:")) {
                      const scopeId = value.slice("scope:".length);
                      await scopeMutation.mutateAsync(scopeId);
                      if (isOrganizationManagementRoute) {
                        await navigate({
                          to: "/administration/scopes/$scopeId",
                          params: { scopeId },
                        });
                      }
                    }
                  })();
                }}
              >
                {currentScope === null && !allOrganizations ? (
                  <option value="">Выберите организацию</option>
                ) : null}
                {availableScopes.map((scope) => (
                  <option key={scope.id} value={`scope:${scope.id}`}>
                    {scope.name}
                  </option>
                ))}
                {availableScopes.length > 1 ? (
                  <option value={ALL_ORGANIZATIONS}>Все организации</option>
                ) : null}
              </Select>
            </label>
          ) : null}
          <button
            className="user-summary"
            type="button"
            onClick={() => setProfileOpen(true)}
            aria-label="Открыть мой профиль"
          >
            <HeaderAvatar user={user} services={services} />
            <span className="user-copy">
              <strong>{user.display_name}</strong>
              <small>{user.position ?? user.email}</small>
            </span>
          </button>
          {canViewNotifications &&
          services.notifications !== undefined &&
          services.apiClient !== undefined ? (
            <NotificationCenter
              services={
                services as Parameters<typeof NotificationCenter>[0]["services"]
              }
              userId={user.id}
            />
          ) : null}
        </div>
      </header>

      {profileOpen ? (
        <ProfileCard
          user={user}
          services={services}
          onClose={() => setProfileOpen(false)}
          onLogout={() => logoutMutation.mutate()}
          loggingOut={logoutMutation.isPending}
        />
      ) : null}

      {mobileMenuOpen ? (
        <div
          className="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Навигация ERP"
        >
          <button
            className="mobile-menu__backdrop"
            aria-label="Закрыть меню"
            type="button"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside
            className="mobile-menu__panel"
            id="mobile-navigation"
            ref={mobileMenuPanel}
            tabIndex={-1}
          >
            <div className="mobile-menu__top">
              <ErpLogo
                label="Алтайэкопарк ERP — перейти к обзору"
                onNavigate={() => setMobileMenuOpen(false)}
              />
              <CloseButton
                label="Закрыть меню"
                onClick={() => setMobileMenuOpen(false)}
              />
            </div>
            {nav}
            {accountNav}
          </aside>
        </div>
      ) : null}

      <main className="erp-main">
        <Outlet />
      </main>
    </div>
  );
}

function SidebarNavigationLink({
  item,
}: {
  item: ReturnType<typeof workspaceNavigation>[number];
}) {
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      activeProps={{ className: "sidebar-link sidebar-link--active" }}
      className="sidebar-link"
    >
      <span className="nav-icon" aria-hidden="true">
        <Icon />
      </span>
      <span>{item.label}</span>
    </Link>
  );
}
